import React from 'react';
import { X, Trophy, Target, TrendingUp, Crown, Award, Zap } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { DesignerReportDownload } from './DesignerReportDownload';

const LEVEL_STYLE = {
  Outstanding: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'Strong Performer': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'Reliable Performer': 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  'Needs Improvement': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'Performance Concern': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'Not Scored': 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const MetricBar = ({ value, max = 100, color = 'bg-[#331062]' }) => (
  <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
    <div
      className={cn('h-full rounded-full transition-all', color)}
      style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
    />
  </div>
);

const StatRow = ({ label, value, suffix, bar, barMax = 100, barColor, highlight }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-baseline text-xs">
      <span className="text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</span>
      <span className={cn('font-bold text-sm', highlight || 'text-slate-800 dark:text-white')}>
        {value}
        {suffix && <span className="text-xs text-slate-400 ml-0.5">{suffix}</span>}
      </span>
    </div>
    {bar !== undefined && <MetricBar value={bar} max={barMax} color={barColor} />}
  </div>
);

// Compute who's the "winner" per metric — used to highlight best value in each row
const findBest = (designers, key, higherIsBetter = true) => {
  if (!designers.length) return null;
  const sorted = [...designers].sort((a, b) =>
    higherIsBetter ? (b[key] || 0) - (a[key] || 0) : (a[key] || 0) - (b[key] || 0)
  );
  return sorted[0]?.designer_id;
};

export const CompareDesignersDialog = ({ open, onOpenChange, designers = [] }) => {
  const bestPerf = findBest(designers, 'performance_score');
  const bestContrib = findBest(designers, 'contribution_score');
  const bestRanking = findBest(designers, 'ranking_score');
  const bestActive = findBest(designers, 'avg_active_days');

  const getHighlight = (designerId, bestId) =>
    designerId === bestId ? 'text-emerald-600 dark:text-emerald-400' : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[92vw] md:max-w-6xl max-h-[90vh] overflow-y-auto"
        data-testid="compare-designers-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-outfit">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Designer Comparison
            <span className="text-sm font-normal text-slate-500 ml-2">
              ({designers.length} selected)
            </span>
          </DialogTitle>
        </DialogHeader>

        {designers.length < 2 ? (
          <p className="text-sm text-slate-500 py-8 text-center">
            Select 2 or more designers to compare.
          </p>
        ) : (
          <div
            className={cn(
              'grid gap-4 mt-4',
              designers.length === 2 && 'grid-cols-1 md:grid-cols-2',
              designers.length === 3 && 'grid-cols-1 md:grid-cols-3',
              designers.length >= 4 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            )}
          >
            {designers.map((d) => {
              const initials = (d.designer_name || '?')
                .split(' ')
                .map((s) => s[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();
              const levelClass = LEVEL_STYLE[d.performance_level] || LEVEL_STYLE['Not Scored'];

              return (
                <div
                  key={d.designer_id}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800 shadow-sm flex flex-col gap-4"
                  data-testid={`compare-card-${d.designer_id}`}
                >
                  {/* Profile header */}
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#331062] to-[#4a1d8a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 dark:text-white truncate">
                        {d.designer_name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {d.designer_role}
                      </p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate mt-0.5">
                        {d.employee_id} · {d.sub_team || '-'} · {d.location || '-'}
                      </p>
                    </div>
                    <DesignerReportDownload
                      designerId={d.designer_id}
                      designerName={d.designer_name}
                      testIdPrefix="compare-report"
                    />
                  </div>

                  {/* Performance level badge */}
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold',
                        levelClass
                      )}
                    >
                      {d.performance_level || d.ranking_status || 'Unranked'}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Rank #{d.filtered_rank || d.rank || '-'}
                    </span>
                  </div>

                  {/* Score grid */}
                  <div className="space-y-3">
                    <StatRow
                      label="Ranking Score"
                      value={d.ranking_score ?? 0}
                      suffix="/100"
                      bar={d.ranking_score}
                      barColor="bg-gradient-to-r from-[#331062] to-[#4a1d8a]"
                      highlight={getHighlight(d.designer_id, bestRanking) || 'text-[#331062] dark:text-purple-400'}
                    />
                    <StatRow
                      label="Performance"
                      value={d.performance_score ?? 0}
                      suffix="/100"
                      bar={d.performance_score}
                      barColor="bg-blue-500"
                      highlight={getHighlight(d.designer_id, bestPerf)}
                    />
                    <StatRow
                      label="Contribution"
                      value={`${d.contribution_score ?? 0}%`}
                      bar={d.contribution_score}
                      barColor="bg-green-500"
                      highlight={getHighlight(d.designer_id, bestContrib)}
                    />
                    <StatRow
                      label="Avg Active Days"
                      value={d.avg_active_days ?? 0}
                      suffix="/week"
                      bar={d.avg_active_days}
                      barMax={7}
                      barColor="bg-amber-500"
                      highlight={getHighlight(d.designer_id, bestActive)}
                    />
                  </div>

                  {/* Bottom stats */}
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400">Weeks</p>
                      <p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">
                        {d.weeks_scored ?? 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400">KRA</p>
                      <p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">
                        {(d.kra_score ?? 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400">+/-</p>
                      <p className="text-sm font-bold mt-0.5">
                        <span className="text-emerald-500">{d.total_positive_feedback ?? 0}</span>
                        <span className="text-slate-400 mx-0.5">/</span>
                        <span className="text-red-500">{d.total_escalations ?? 0}</span>
                      </p>
                    </div>
                  </div>

                  {/* Winner badge (if wins most metrics) */}
                  {[bestPerf, bestContrib, bestRanking].filter((b) => b === d.designer_id).length >= 2 && (
                    <div className="flex items-center gap-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-2.5 py-1.5">
                      <Crown className="w-3.5 h-3.5 text-yellow-600" />
                      <span className="text-xs font-semibold text-yellow-800 dark:text-yellow-300">
                        Leads on most metrics
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end mt-6 gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="compare-close-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompareDesignersDialog;
